// Tanks imported from wiiplaytanks, fighting with per-type FUNCTIONS. One of every mobile type
// per team drives the map, colored by team. Each type has a fire MODE + move MODE (see types.ts):
//   direct/rocket/sniper  straight (bouncing) shells, require LINE OF SIGHT
//   arc (mortar)          lobbed parabolic shells, NO LOS, long range, blast (AoE) on impact
//   seek (tow)            homing missiles, kite
//   ram (kamikaze)        no shell — charge the target and detonate on contact
//   none (support)        no weapon yet — advance as an armored body
// Line-of-sight (nav.heightAt sampling) stops direct-fire tanks from planting and shooting a wall.

import * as THREE from "three";
import { loadTankGeometries, buildTank, type BuiltTank, type TankGeometries } from "../tank/prefab";
import { TANK_TYPES, type FireMode } from "../tank/types";
import { buildNav, findPath, smoothPath, routeVia, laneVias, type NavGraph, type Pt } from "../systems/pathfind";
import { TEAM_BLUE, TEAM_RED } from "../config";
import { sfx } from "../engine/sfx";
import { Projectiles, type Combatant, type CombatSide, type DamageType } from "../systems/projectiles";
import { balance } from "../systems/balance";
import { tankUnitCost, type UnitBank } from "../systems/economy";
import type { HealthBars } from "../systems/healthbars";
import type { GeneratedLevel } from "../map/grammar";
import type { Army } from "./army";

const TANK_SCALE = 2.4; // ~20-stud tank -> ~48-stud footprint (~2x a little-person)
const TANK_SPEED = 105; // studs/sec — advance WITH the army, not racing ahead of the front
const SPAWN_INTERVAL = 1.4;
const ARRIVE = 90;
const TURN_RATE = 3.2; // body yaw ease (rad/s)
const TURRET_RATE = 4.2; // turret yaw ease (rad/s)
const UNSTICK_T = 1.2;

const TEAM_HEX = [TEAM_BLUE.getHex(), TEAM_RED.getHex()];
const TANK_HITR = 20; // shell hit radius against a tank (studs)
const TANK_SEP = 50; // min center-to-center spacing so hulls don't intersect (~footprint)
const ACQUIRE = 1200; // base target acquisition range (studs); scaled by def.rangeMul
const FIRE_RANGE = 800; // base range at which a tank plants + fires; scaled by def.rangeMul
const AIM_TOL = 0.14; // radians the turret must be within before a direct shot fires
const LOS_STEP = 40; // line-of-sight sampling step (studs)

const KITE_RANGE = 520; // kiters (stalker/tow) try to keep at least this much distance
const KITE_SPEED = 95; // back-off speed for kiters
const CHARGE_SPEED = 240; // kamikaze charge speed (studs/s)
const RAM_CONTACT = 48; // kamikaze detonation contact distance

const MUZZLE_FWD = 15;
const MUZZLE_UP = 6.875; // MUZZLE_LOCAL.y — barrel height above the tank floor (pre-scale)
const ARC_ELEV = 0.9; // mortar barrel elevation (rad, ~51deg lob)
const BULLET_SCALE = 4; // scale source shell speeds into the 2x world
const BULLET_DAMAGE = 60; // per direct hit
const WALL_UP = 60; // floor rising more than this ahead => wall => blocks LOS
const WALL_DOWN = 150; // floor dropping more than this => gap => blocks LOS

// arcing mortar (artillery) — durations used for the turret lead + the lob land point
const ARC_TIME_PER = 380;
const ARC_DUR_MIN = 0.9;
const ARC_DUR_MAX = 2.6;
const ARC_PEAK = 320; // apex height of a lob (studs)
const EXPLODE_DAMAGE = 100; // AoE damage — instakills fighters, chunks tanks

const UP = new THREE.Vector3(0, 1, 0);
const TILT_SAMPLE = 24;

const LOS_MODES: Record<FireMode, boolean> = {
  direct: true, rocket: true, sniper: true, seek: true, arc: false, ram: false, none: false,
};

interface TankUnit {
  def: (typeof TANK_TYPES)[number];
  built: BuiltTank;
  team: 0 | 1;
  active: boolean;
  dead: boolean;
  hp: number;
  maxHp: number; // spawn health (for the health bar fraction)
  hitR: number;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  px: number;
  pz: number;
  path: Pt[];
  wi: number;
  bodyYaw: number;
  turretYaw: number;
  turretPitch: number; // barrel elevation (rad, +up) — vertical aim toward the target's height
  spin: number; // gatling barrel-cluster spin ramp (0..1)
  noMoveT: number;
  target: Combatant | null;
  acqCd: number;
  fireCd: number;
  shellsOut: number;
  gen: number;
}

export class Tanks implements CombatSide {
  private group = new THREE.Group();
  private nav: NavGraph | null = null;
  private pool: TankUnit[] = [];
  private projectiles: Projectiles | null = null;
  private spawnTimer = 0;
  autoSpawn = true; // false = no timer spawns (base-building mode; tanks are a later assembly tier)
  private ready = false;
  private army: Army | null = null;
  private healthBars: HealthBars | null = null;
  private laneCursor: [number, number] = [0, 0];
  private _normal = new THREE.Vector3();
  private _eul = new THREE.Euler();
  private _qTilt = new THREE.Quaternion();
  private _qYaw = new THREE.Quaternion();
  private _qTarget = new THREE.Quaternion();
  private _qTurret = new THREE.Quaternion();

  constructor(scene: THREE.Scene) {
    this.group.name = "tanks";
    scene.add(this.group);
  }

  setArmy(army: Army): void {
    this.army = army;
  }

  setProjectiles(p: Projectiles): void {
    this.projectiles = p;
  }

  private banks: UnitBank[] | null = null; // shared per-team UP pools (tanks are never free)

  /** Share the per-team Unit-Point banks (every tank costs UP, priced by its base hp). */
  setBanks(banks: UnitBank[]): void {
    this.banks = banks;
  }

  setHealthBars(hb: HealthBars): void {
    this.healthBars = hb;
  }

  /** Read-only nav (so main can share it with the projectile system). */
  get navGraph(): NavGraph | null {
    return this.nav;
  }

  private targetSides: CombatSide[] = []; // extra enemy sources (e.g. the player) tanks may target

  /** Register a side (e.g. the player) that tanks may also acquire + fire at. */
  addTargetSide(side: CombatSide): void {
    this.targetSides.push(side);
  }

  // ---- CombatSide: tanks are targetable + damageable by any projectile ----
  units(): readonly Combatant[] {
    return this.pool;
  }
  damage(u: Combatant, dmg: number, type: DamageType): void {
    const mul = type === "bullet" ? balance.tankBulletDmg : type === "explosion" ? balance.tankBlastDmg : 1;
    this.damageTank(u as TankUnit, dmg * mul);
  }

  async load(): Promise<void> {
    let geo: TankGeometries;
    try {
      geo = await loadTankGeometries();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[tanks] geometry load failed", e);
      return;
    }
    for (const def of TANK_TYPES) {
      for (const team of [0, 1] as const) {
        const built = buildTank(geo, TEAM_HEX[team], false, def.id, 0);
        if (def.turretless) built.turretPivot.visible = false;
        built.root.scale.setScalar(TANK_SCALE);
        built.root.visible = false;
        built.root.traverse((o) => {
          if (o instanceof THREE.Mesh) o.castShadow = true;
        });
        this.group.add(built.root);
        this.pool.push({
          def, built, team, active: false, dead: false, hp: def.hp, maxHp: def.hp, hitR: TANK_HITR,
          pos: new THREE.Vector3(), vel: new THREE.Vector3(), px: 0, pz: 0, path: [], wi: 1,
          bodyYaw: 0, turretYaw: 0, turretPitch: 0, spin: 0, noMoveT: 0, target: null, acqCd: 0, fireCd: 0, shellsOut: 0, gen: 0,
        });
      }
    }

    this.ready = true;
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  /** Mirrors Army.renderGroups: the roots to hide in the cheap re-render passes (bloom occluder + reflection). */
  get renderGroups(): THREE.Object3D[] { return [this.group]; }

  setLevel(lvl: GeneratedLevel, navOverride?: NavGraph): void {
    for (const u of this.pool) {
      u.active = false;
      u.dead = false;
      u.target = null;
      u.shellsOut = 0;
      u.built.root.visible = false;
    }
    this.spawnTimer = 0;
    this.nav = navOverride ?? buildNav(lvl); // terra battles hand in their prebuilt open-field nav
  }

  private spawn(): void {
    const nav = this.nav;
    if (!nav) return;
    if (this.pool.reduce((n, u) => n + (u.active ? 1 : 0), 0) >= balance.tankMax) return; // rarity cap
    const slot = this.pool.find((u) => !u.active);
    if (!slot) return;
    // UP gate: every tank costs Unit Points (priced by base hp). Can't afford → bank the UP, no tank.
    const bank = this.banks ? this.banks[slot.team] : null;
    const cost = tankUnitCost(slot.def.hp);
    if (bank && !bank.canAfford(cost)) return;
    const from = slot.team === 0 ? nav.startKeys : nav.coreKeys;
    const to = slot.team === 0 ? nav.coreKeys : nav.startKeys;
    if (!from.length || !to.length) return;
    const fromKey = from[(Math.random() * from.length) | 0];
    const toKey = to[(Math.random() * to.length) | 0];
    const vias = laneVias(nav, slot.team === 0, this.laneCursor[slot.team]++);
    let path = routeVia(nav, fromKey, vias, toKey);
    if (!path) {
      const cellPath = findPath(nav, fromKey, toKey);
      if (!cellPath || cellPath.length < 2) return;
      path = smoothPath(nav, cellPath);
    }
    if (path.length < 2) return;
    slot.path = path;
    slot.wi = 1;
    slot.pos.set(path[0].x, path[0].y, path[0].z);
    slot.bodyYaw = 0;
    slot.turretYaw = 0;
    slot.turretPitch = 0;
    slot.spin = 0;
    slot.noMoveT = 0;
    slot.hp = slot.def.hp * balance.tankHpMul;
    slot.maxHp = slot.hp;
    slot.dead = false;
    slot.target = null;
    slot.acqCd = Math.random() * 0.3;
    slot.fireCd = slot.def.fire ? Math.random() * slot.def.fire.interval : 0;
    slot.shellsOut = 0;
    slot.gen++;
    if (bank) bank.spend(cost); // commit the UP now the tank is going through
    slot.active = true;
    slot.built.root.visible = true;
  }

  private slideMove(u: TankUnit, dx: number, dz: number): number {
    const nav = this.nav!;
    if (nav.heightAt(u.pos.x + dx, u.pos.z + dz) != null) {
      u.pos.x += dx;
      u.pos.z += dz;
      return Math.hypot(dx, dz);
    }
    if (dx !== 0 && nav.heightAt(u.pos.x + dx, u.pos.z) != null) {
      u.pos.x += dx;
      return Math.abs(dx);
    }
    if (dz !== 0 && nav.heightAt(u.pos.x, u.pos.z + dz) != null) {
      u.pos.z += dz;
      return Math.abs(dz);
    }
    return 0;
  }

  /** Is the segment a->b clear of walls/gaps at floor height fy? (shell can roughly reach) */
  private losClear(ax: number, az: number, bx: number, bz: number, fy: number): boolean {
    const dx = bx - ax;
    const dz = bz - az;
    const dist = Math.hypot(dx, dz);
    const steps = Math.max(1, Math.ceil(dist / LOS_STEP));
    // TERRA: sight-line vs the smooth ground (the fixed-fy wall test reads every uphill slope as a
    // wall → direct-fire tanks would go blind on rolling terrain; see terra design review).
    const shot = this.nav?.shotHeightAt;
    if (shot) {
      const hb = shot(bx, bz);
      if (hb == null) return false;
      const eyeA = fy + 26, eyeB = hb + 20;
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        const h = shot(ax + dx * t, az + dz * t);
        if (h == null || h > eyeA + (eyeB - eyeA) * t) return false;
      }
      return true;
    }
    for (let s = 1; s < steps; s++) {
      if (this.blocked(ax + (dx * s) / steps, az + (dz * s) / steps, fy)) return false;
    }
    return true;
  }

  /** Nearest enemy unit within range; LOS-filtered for direct-fire modes (never a target behind a wall). */
  private acquire(u: TankUnit): Combatant | null {
    const R = ACQUIRE * (u.def.rangeMul ?? 1);
    const needLOS = LOS_MODES[u.def.mode];
    let best: Combatant | null = null;
    let bestD2 = R * R;
    const consider = (c: Combatant): void => {
      const d2 = (c.pos.x - u.pos.x) ** 2 + (c.pos.z - u.pos.z) ** 2;
      if (d2 < bestD2 && (!needLOS || this.losClear(u.pos.x, u.pos.z, c.pos.x, c.pos.z, u.pos.y))) {
        bestD2 = d2;
        best = c;
      }
    };
    for (const t of this.pool) {
      if (t === u || !t.active || t.dead || t.team === u.team) continue;
      consider(t);
    }
    if (this.army) for (const f of this.army.targets()) {
      if (f.dead || f.team === u.team) continue;
      consider(f);
    }
    for (const side of this.targetSides) {
      for (const c of side.units()) {
        if (c.dead || c.team === u.team || c.active === false) continue;
        consider(c);
      }
    }
    return best;
  }

  private muzzle(u: TankUnit, out: { x: number; z: number; fx: number; fz: number }, jitter: number): void {
    const yaw = u.turretYaw + (Math.random() * 2 - 1) * jitter;
    out.fx = -Math.sin(yaw);
    out.fz = -Math.cos(yaw);
    out.x = u.pos.x + out.fx * MUZZLE_FWD * TANK_SCALE;
    out.z = u.pos.z + out.fz * MUZZLE_FWD * TANK_SCALE;
  }
  private _m = { x: 0, z: 0, fx: 0, fz: 0 };

  /** direct / rocket / sniper: a straight (bouncing) shell into the shared projectile system. */
  private fireShell(u: TankUnit): boolean {
    const p = this.projectiles;
    if (!p) return false;
    const f = u.def.fire!;
    this.muzzle(u, this._m, f.aimError);
    const size = u.def.mode === "sniper" ? 0.9 : 1;
    if (!p.fireStraight(this._m.x, this._m.z, u.pos.y, this._m.fx, this._m.fz, f.speed * BULLET_SCALE, u.team, BULLET_DAMAGE, f.bounces, 0, size, u.team, u)) return false;
    sfx.play("fire", u.pos);
    return true;
  }

  /** seek (tow): a homing missile. */
  private fireSeek(u: TankUnit): boolean {
    const p = this.projectiles;
    if (!p || !u.target) return false;
    const f = u.def.fire!;
    this.muzzle(u, this._m, f.aimError);
    if (!p.fireSeek(this._m.x, this._m.z, u.pos.y, this._m.fx, this._m.fz, f.speed * BULLET_SCALE, u.team, EXPLODE_DAMAGE, 70, u.target, u)) return false;
    sfx.play("fire", u.pos);
    return true;
  }

  /** arc (artillery = mortar): lob to the target's lead point; no LOS needed. */
  private fireArc(u: TankUnit): boolean {
    const p = this.projectiles;
    const t = u.target;
    if (!p || !t) return false;
    const f = u.def.fire!;
    this.muzzle(u, this._m, 0);
    const range = Math.hypot(t.pos.x - u.pos.x, t.pos.z - u.pos.z) || 1;
    const dur = Math.max(ARC_DUR_MIN, Math.min(ARC_DUR_MAX, range / ARC_TIME_PER));
    const leadK = dur * (0.5 + f.lead);
    const jit = f.aimError * range;
    const tx = t.pos.x + t.vel.x * leadK + (Math.random() * 2 - 1) * jit;
    const tz = t.pos.z + t.vel.z * leadK + (Math.random() * 2 - 1) * jit;
    if (!p.fireArc(this._m.x, this._m.z, u.pos.y, tx, tz, u.team, EXPLODE_DAMAGE, u.def.blastR ?? 130, ARC_PEAK, u)) return false;
    sfx.play("artillery", u.pos);
    return true;
  }

  private blocked(nx: number, nz: number, fy: number): boolean {
    const h = this.nav!.heightAt(nx, nz);
    return h == null || h - fy > WALL_UP || fy - h > WALL_DOWN;
  }

  private damageTank(t: TankUnit, dmg: number): void {
    if (t.dead) return;
    t.hp -= dmg;
    if (t.hp <= 0) {
      t.dead = true;
      t.active = false;
      t.target = null;
      t.built.root.visible = false;
      sfx.play("explode", t.pos); // the tank brews up
    }
  }

  update(dt: number): void {
    if (!this.ready || !this.nav || !this.group.visible) return;
    if (dt < 1e-4) return;
    if (dt > 1 / 30) dt = 1 / 30;
    const nav = this.nav;

    if (this.autoSpawn) {
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0) {
        this.spawnTimer = SPAWN_INTERVAL;
        this.spawn();
      }
    }

    for (const u of this.pool) {
      if (!u.active) continue;
      u.px = u.pos.x;
      u.pz = u.pos.z;
      const def = u.def;
      const mode = def.mode;
      const f = def.fire;
      const rm = def.rangeMul ?? 1;
      const acqR = ACQUIRE * rm;
      const fireR = FIRE_RANGE * rm;
      const needLOS = LOS_MODES[mode];

      // ---- retire once the far end is reached (driving OR planted) ----
      if (u.wi >= u.path.length - 1) {
        const dwx = u.path[u.wi].x - u.pos.x, dwz = u.path[u.wi].z - u.pos.z;
        if (dwx * dwx + dwz * dwz < ARRIVE * ARRIVE) { // squared compare — no sqrt
          u.active = false;
          u.target = null;
          u.built.root.visible = false;
          continue;
        }
      }

      // ---- acquire; drop a target that died, left range, or (LOS modes) ducked behind cover ----
      u.acqCd -= dt;
      if (u.target) {
        const d2 = (u.target.pos.x - u.pos.x) ** 2 + (u.target.pos.z - u.pos.z) ** 2;
        if (u.target.dead || d2 > acqR * acqR || (needLOS && !this.losClear(u.pos.x, u.pos.z, u.target.pos.x, u.target.pos.z, u.pos.y))) {
          u.target = null;
        }
      }
      if (u.acqCd <= 0) {
        u.acqCd = 0.3;
        u.target = this.acquire(u);
      }
      const targetDist = u.target ? Math.hypot(u.target.pos.x - u.pos.x, u.target.pos.z - u.pos.z) : Infinity;
      // "engaged" = holding position to fire (advance plants, kite holds/backs off). Arc engages at
      // any range within acqR (it lobs); charge/none never plant.
      const engaged = !!u.target && mode !== "none" && def.move !== "charge" && (mode === "arc" || targetDist <= fireR);

      // ---- movement by move mode ----
      if (def.move === "charge") {
        // kamikaze: detonate on contact; rush the target in a straight line only when it's in
        // the OPEN (LOS clear) — otherwise advance the path so it routes around walls instead of
        // pressing helplessly into one.
        if (u.target && targetDist < RAM_CONTACT) {
          this.projectiles?.explode(u.pos.x, u.pos.z, u.pos.y, u.team, def.blastR ?? 100, EXPLODE_DAMAGE);
          u.active = false;
          u.dead = true;
          u.built.root.visible = false;
          continue;
        }
        if (u.target && this.losClear(u.pos.x, u.pos.z, u.target.pos.x, u.target.pos.z, u.pos.y)) {
          const dx = u.target.pos.x - u.pos.x;
          const dz = u.target.pos.z - u.pos.z;
          const inv = 1 / (targetDist || 1);
          const adv = Math.min(CHARGE_SPEED * dt, targetDist);
          const moved = this.slideMove(u, dx * inv * adv, dz * inv * adv);
          this.faceBody(u, dx, dz, dt);
          // measure REAL progress: on terra the eye-line sees over an unwalkable crevasse/pool the
          // hull can't cross — a wedged charger must drop the target and route around (review)
          if (moved < adv * 0.5) {
            u.noMoveT += dt;
            if (u.noMoveT > UNSTICK_T) {
              u.target = null;
              u.acqCd = 1.5; // don't instantly re-acquire the same unreachable unit and re-wedge
              u.noMoveT = 0;
            }
          } else {
            u.noMoveT = 0;
          }
        } else {
          this.drivePath(u, dt);
        }
      } else if (engaged && def.move === "kite" && targetDist < KITE_RANGE) {
        // kiter too close: back away from the target while still firing
        const dx = u.pos.x - u.target!.pos.x;
        const dz = u.pos.z - u.target!.pos.z;
        const inv = 1 / (targetDist || 1);
        this.slideMove(u, dx * inv * KITE_SPEED * dt, dz * inv * KITE_SPEED * dt);
        this.faceBody(u, u.target!.pos.x - u.pos.x, u.target!.pos.z - u.pos.z, dt);
        u.noMoveT = 0;
      } else if (engaged) {
        // plant (advance) / hold at range (kite): face the target, hold position
        u.noMoveT = 0;
        this.faceBody(u, u.target!.pos.x - u.pos.x, u.target!.pos.z - u.pos.z, dt);
      } else {
        this.drivePath(u, dt);
      }

      const gy = nav.heightAt(u.pos.x, u.pos.z);
      if (gy != null) u.pos.y += (gy - u.pos.y) * Math.min(1, 12 * dt);
      u.vel.set((u.pos.x - u.px) / dt, 0, (u.pos.z - u.pz) / dt);

      // ---- turret aim + fire (skipped for turretless / no-weapon types) ----
      if (u.target && f) {
        const turretLerp = Math.min(1, TURRET_RATE * dt); // shared by yaw + pitch aim below
        const rdx = u.target.pos.x - u.pos.x;
        const rdz = u.target.pos.z - u.pos.z;
        const range = Math.hypot(rdx, rdz) || 1;
        const tof = mode === "arc"
          ? Math.max(ARC_DUR_MIN, Math.min(ARC_DUR_MAX, range / ARC_TIME_PER))
          : range / (f.speed * BULLET_SCALE);
        const leadK = tof * (0.5 + f.lead);
        const aimX = u.target.pos.x + u.target.vel.x * leadK;
        const aimZ = u.target.pos.z + u.target.vel.z * leadK;
        const want = Math.atan2(-(aimX - u.pos.x), -(aimZ - u.pos.z));
        let td = want - u.turretYaw;
        while (td > Math.PI) td -= Math.PI * 2;
        while (td < -Math.PI) td += Math.PI * 2;
        u.turretYaw += td * turretLerp;

        // vertical aim: elevate the barrel toward the target's height (mortars lob steeply)
        const muzzleY = u.pos.y + MUZZLE_UP * TANK_SCALE;
        const wantPitch = mode === "arc"
          ? ARC_ELEV
          : Math.max(-0.45, Math.min(0.6, Math.atan2(u.target.pos.y + 30 - muzzleY, range)));
        u.turretPitch += (wantPitch - u.turretPitch) * turretLerp;

        u.fireCd -= dt;
        // arc lobs without needing a tight bearing; direct fire needs the barrel on-target
        const aimed = mode === "arc" || Math.abs(td) < AIM_TOL;
        if (engaged && aimed && u.fireCd <= 0 && u.shellsOut < f.shells) {
          const fired = mode === "arc" ? this.fireArc(u) : mode === "seek" ? this.fireSeek(u) : this.fireShell(u);
          if (fired) u.fireCd = f.interval; // pool full => keep cooldown, retry next frame
        }
      } else if (!def.turretless) {
        u.turretYaw += dt * 0.35; // idle scan
        u.turretPitch += (0 - u.turretPitch) * Math.min(1, TURRET_RATE * dt); // settle level
        if (f) u.fireCd -= dt;
      }

      // gatling: spin the barrel cluster up while engaged, coast down otherwise
      if (u.built.spinBarrel) {
        u.spin += ((engaged ? 1 : 0) - u.spin) * Math.min(1, dt * 5);
        u.built.spinBarrel.rotation.z += u.spin * 22 * dt;
      }

      this.syncTank(u, dt);
      this.healthBars?.add(u.pos.x, u.pos.y + 46, u.pos.z, u.hp / u.maxHp);
    }

    // keep hulls from intersecting: push overlapping tanks apart (dt-capped, walkable-clamped)
    for (let i = 0; i < this.pool.length; i++) {
      const a = this.pool[i];
      if (!a.active) continue;
      for (let j = i + 1; j < this.pool.length; j++) {
        const b = this.pool[j];
        if (!b.active) continue;
        const dx = b.pos.x - a.pos.x;
        const dz = b.pos.z - a.pos.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < TANK_SEP * TANK_SEP && d2 > 1e-3) {
          const d = Math.sqrt(d2);
          const push = Math.min((TANK_SEP - d) * 0.5, TANK_SPEED * dt);
          const ux = dx / d;
          const uz = dz / d;
          this.slideMove(a, -ux * push, -uz * push);
          this.slideMove(b, ux * push, uz * push);
        }
      }
    }

    // continuous loops: drive (nearest MOVING tank) + turretspin (nearest tank tracking a target),
    // one voice each, so flying near the armor gives treads + servo without stacking 30 engines.
    const lp = sfx.listenerPos();
    let driveTank: TankUnit | null = null;
    let driveD = Infinity;
    let turretTank: TankUnit | null = null;
    let turretD = Infinity;
    for (const u of this.pool) {
      if (!u.active) continue;
      const d = (u.pos.x - lp.x) ** 2 + (u.pos.z - lp.z) ** 2;
      if (u.vel.x ** 2 + u.vel.z ** 2 > 400 && d < driveD) { // squared speed compare — no sqrt
        driveD = d;
        driveTank = u;
      }
      if (u.target && u.def.fire && d < turretD) {
        turretD = d;
        turretTank = u;
      }
    }
    sfx.sustain("drive", !!driveTank, driveTank?.pos);
    sfx.sustain("turretspin", !!turretTank, turretTank?.pos);
  }

  /** Ease the hull yaw toward a heading (dx,dz). */
  private faceBody(u: TankUnit, dx: number, dz: number, dt: number): void {
    let d = Math.atan2(dx, dz) - u.bodyYaw;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    u.bodyYaw += d * Math.min(1, TURN_RATE * dt);
  }

  /** Drive along the path toward the far end (with waypoint-skip + unstick). */
  private drivePath(u: TankUnit, dt: number): void {
    let dxp = u.path[u.wi].x - u.pos.x;
    let dzp = u.path[u.wi].z - u.pos.z;
    let dist = Math.hypot(dxp, dzp);
    while (dist < ARRIVE && u.wi < u.path.length - 1) {
      u.wi++;
      dxp = u.path[u.wi].x - u.pos.x;
      dzp = u.path[u.wi].z - u.pos.z;
      dist = Math.hypot(dxp, dzp);
    }
    const adv = Math.min(TANK_SPEED * dt, dist);
    const moved = adv > 0 ? this.slideMove(u, (dxp / dist) * adv, (dzp / dist) * adv) : 0;
    this.faceBody(u, dxp, dzp, dt);
    if (moved < adv * 0.5 + 0.01) {
      u.noMoveT += dt;
      if (u.noMoveT > UNSTICK_T && u.wi < u.path.length - 1) {
        u.wi++;
        u.noMoveT = 0;
      }
    } else {
      u.noMoveT = 0;
    }
  }

  /** Position + slope-aligned hull orientation + world-yaw turret. */
  private syncTank(u: TankUnit, dt: number): void {
    const nav = this.nav!;
    u.built.root.position.set(u.pos.x, u.pos.y, u.pos.z);
    const hx1 = nav.heightAt(u.pos.x + TILT_SAMPLE, u.pos.z);
    const hx0 = nav.heightAt(u.pos.x - TILT_SAMPLE, u.pos.z);
    const hz1 = nav.heightAt(u.pos.x, u.pos.z + TILT_SAMPLE);
    const hz0 = nav.heightAt(u.pos.x, u.pos.z - TILT_SAMPLE);
    this._eul.set(0, u.bodyYaw, 0);
    this._qYaw.setFromEuler(this._eul);
    if (hx1 != null && hx0 != null && hz1 != null && hz0 != null) {
      const sx = (hx1 - hx0) / (2 * TILT_SAMPLE);
      const sz = (hz1 - hz0) / (2 * TILT_SAMPLE);
      this._normal.set(-sx, 1, -sz).normalize();
      this._qTilt.setFromUnitVectors(UP, this._normal);
      this._qTarget.multiplyQuaternions(this._qTilt, this._qYaw);
    } else {
      this._qTarget.copy(this._qYaw);
    }
    u.built.root.quaternion.slerp(this._qTarget, Math.min(1, 10 * dt));
    // turret = world yaw (canceling hull tilt) THEN barrel pitch about its own X (order YXZ)
    this._eul.set(u.turretPitch, u.turretYaw, 0, "YXZ");
    this._qTurret.setFromEuler(this._eul);
    u.built.turretPivot.quaternion.copy(u.built.root.quaternion).invert().multiply(this._qTurret);
  }
}
